// Header badge that shows the yacht's authoritative server time —
// operators can spot a device-clock drift at a glance without having
// to open a terminal. Never used as a scheduling input; every booking
// still resolves through `getServerNow()` on the backend side.
//
// Renders:
//   ┌──────────────────────────────────────────┐
//   │  🕐  Wed 14 Feb · 14:32 EEST · yacht clock │
//   │      Your device is 47 s ahead            │  ← only when |drift| > 60 s
//   └──────────────────────────────────────────┘

import React from "react";
import { Clock } from "lucide-react";
import { useServerClock } from "@/lib/serverClock";
import { formatInTz } from "@/lib/tz";

// Local: derive the timezone abbreviation (EEST / CET / UTC) for a given
// instant + IANA zone. Falls back to the zone name when Intl doesn't emit
// a %Z-style abbreviation (e.g. `Etc/GMT+2`).
function tzAbbrLocal(iso, zone) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone, timeZoneName: "short",
    }).formatToParts(new Date(iso));
    const tz = parts.find((p) => p.type === "timeZoneName")?.value;
    return tz && !/^GMT/.test(tz) ? tz : (zone || "UTC");
  } catch {
    return zone || "UTC";
  }
}

export default function SystemClock({ compact = false, className = "" }) {
  const { serverNow, driftMs, yachtTz, loading } = useServerClock();
  if (loading) {
    return (
      <div className={`inline-flex items-center gap-2 text-xs text-muted-foreground ${className}`}
           data-testid="system-clock-loading">
        <Clock className="h-3.5 w-3.5 animate-pulse" /> Syncing…
      </div>
    );
  }
  const abbr = tzAbbrLocal(serverNow.toISOString(), yachtTz);
  const dateLabel = formatInTz(
    serverNow.toISOString(),
    { weekday: "short", day: "2-digit", month: "short" },
    yachtTz,
  );
  const timeLabel = formatInTz(
    serverNow.toISOString(),
    { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false },
    yachtTz,
  );
  const driftAbsSec = Math.round(Math.abs(driftMs) / 1000);
  const showDrift = driftAbsSec > 60; // ignore sub-minute noise.
  const ahead = driftMs > 0;

  return (
    <div
      className={`inline-flex flex-col items-start gap-0.5 px-3 py-1.5 border border-border bg-card/70 ${className}`}
      data-testid="system-clock"
      data-drift-seconds={driftAbsSec}
    >
      <div className="flex items-center gap-2 text-xs md:text-sm font-mono tracking-tight">
        <Clock className={`h-3.5 w-3.5 ${showDrift ? "text-amber-500" : "text-accent"}`} />
        {!compact && <span className="text-muted-foreground">{dateLabel}</span>}
        <span className="font-medium">{timeLabel}</span>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{abbr}</span>
      </div>
      {showDrift && (
        <div
          className="text-[10px] text-amber-600 dark:text-amber-400 pl-5"
          data-testid="system-clock-drift"
        >
          Your device is {driftAbsSec}s {ahead ? "ahead" : "behind"} · platform uses ship clock
        </div>
      )}
    </div>
  );
}
